// Shared React Query hooks for organization + location data.
//
// Why this exists: several components independently fetched
// GET /api/organizations and GET /api/retailerlocations/organization/:id with
// raw axios inside useEffects. Any dependency churn (auth/profile/redux state
// settling) turned into a request storm because nothing deduped or cached the
// calls. React Query fixes this structurally:
//   - identical concurrent requests collapse to one (keyed by queryKey),
//   - results are cached (staleTime is set globally to 5 min),
//   - failures respect the global retry cap instead of looping,
//   - in-flight requests are aborted via the query signal.

import { useQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

const API = process.env.REACT_APP_SERVER_DOMAIN;

// Guard against the string forms of empty ids that can leak in from localStorage.
const isValidOrgId = (id) => !!id && id !== 'undefined' && id !== 'null';

// Query keys — exported so mutations can invalidate the matching cache entries.
export const orgKeys = {
  all: ['organizations'],
  detail: (id) => ['organizations', id],
  locations: (orgId) => ['locations', 'byOrg', orgId],
};

// List of all organizations (super-admin only). Pass { enabled: isSuperAdmin() }
// so the request is never issued for users who would get a 403.
export const useOrganizations = ({ enabled = true } = {}) => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: orgKeys.all,
    enabled,
    queryFn: async ({ signal }) => {
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(`${API}/api/organizations`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      return data;
    },
  });
};

// A single organization by id.
export const useOrganization = (organizationId, { enabled = true } = {}) => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: orgKeys.detail(organizationId),
    enabled: enabled && isValidOrgId(organizationId),
    queryFn: async ({ signal }) => {
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(`${API}/api/organizations/${organizationId}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      return data;
    },
  });
};

// Locations belonging to an organization.
export const useOrganizationLocations = (organizationId, { enabled = true } = {}) => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: orgKeys.locations(organizationId),
    enabled: enabled && isValidOrgId(organizationId),
    queryFn: async ({ signal }) => {
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(
        `${API}/api/retailerlocations/organization/${organizationId}`,
        { headers: { Authorization: `Bearer ${token}` }, signal }
      );
      return data;
    },
  });
};
